import { Flags } from '@fullstory/client-api';
import {
  ComputePageFlowResponse,
  PageMetricsServicePublic,
} from '@fullstory/proto/fs/services/alexa';
import * as dxo from '@fullstory/proto/fs/services/dx-objects';
import {
  ArchiveMultiRequest,
  ArchiveRequest,
  AutocompleteRequest,
  GetAllRequest,
  MatchUrlsRequest,
  SearchRequest,
  SetMLStateRequest,
  SetRequest,
  PagesPublic,
} from '@fullstory/proto/fs/services/paige';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import {
  createGRPCAction,
  createRequestCancellationAction,
  RequestActionType,
  RequestDispatch,
} from '@fullstory/redux-util';
import { ActionType, createAppCacheActions } from '@fullstory/redux-util/utils';
import { createAppCacheRequestAction } from '@fullstory/redux-util/utils/appCache/actions';
import { CacheKeyPredicate, defaultCacheKey } from '@fullstory/requests';
import { isDataFresh } from '@fullstory/searchiecache';
import { hashObject } from '@fullstory/util/hash';
import { UID } from '@fullstory/util/uid';
import { BatchOptions } from '@fullstory/util/xhr';
import { AppDispatch } from '../../types';
import { getCacheOptions, metricsCacheOptionsRefresh } from '../metrics';
import { SearchCacheKey } from '../searchMeta';
import { PageFlowRequestBody, PageRequestTypes } from './types';
import { pageFlowSimilarData, pagesRequestBodyToComputePageFlowRequest } from './utils';

export const PageFlowActions = createAppCacheActions(
  PageRequestTypes.PageAppCacheIsLoading,
  PageRequestTypes.PageAppCacheLoadFailed,
  PageRequestTypes.PageAppCacheRefetching,
  PageRequestTypes.PageAppCacheHasData,
  PageRequestTypes.PageAppCacheFresh,
  PageRequestTypes.PageAppCacheSwap,
  PageRequestTypes.PageAppCacheClear,
)<ComputePageFlowResponse>();

const invalidateCacheKey: CacheKeyPredicate = (key: string): boolean =>
  key.startsWith(defaultCacheKey(dxo.AppBackendPublic.GetPageDetailViewEndpoint, undefined)) ||
  key.startsWith(defaultCacheKey(dxo.AppBackendPublic.GetPageSettingsEndpoint, undefined));

export type PageActions = PagesRequestActions | ActionType<typeof PageFlowActions>;
export type PagesRequestActions = RequestActionType<typeof PagesRequestActions>;
export const PagesRequestActions = {
  getAllPages: (orgId: string, force: boolean = false) =>
    createGRPCAction<typeof dxo.PagesPublic.GetAllPages>()(
      {
        type: PageRequestTypes.GetAllPagesRequest,
        grpcFunction: dxo.PagesPublic.GetAllPages,
        body: {
          orgId,
          userDefinedPages: GetAllRequest.Filter.ALL,
          learnedPages: GetAllRequest.Filter.ALL,
          includeSuggested: true,
        },
        force,
        readWrite: 'read',
      },
      PageRequestTypes.GetAllPagesSuccess,
      PageRequestTypes.GetAllPagesFailure,
    ),
  setPage: (req: SetRequest, force: boolean = false) =>
    createGRPCAction<typeof dxo.PagesPublic.SetPage>()(
      {
        type: PageRequestTypes.SetPageRequest,
        grpcFunction: dxo.PagesPublic.SetPage,
        body: req,
        force,
        invalidateCacheKey,
        readWrite: 'write',
      },
      PageRequestTypes.SetPageSuccess,
      PageRequestTypes.SetPageFailure,
    ),
  archivePage: (req: ArchiveRequest, archived: boolean, force: boolean = false) =>
    createGRPCAction<typeof dxo.PagesPublic.ArchivePage>()(
      {
        type: PageRequestTypes.ArchivePageRequest,
        grpcFunction: dxo.PagesPublic.ArchivePage,
        body: { orgId: req.orgId, pageId: req.id, archived },
        force,
        invalidateCacheKey,
        readWrite: 'write',
      },
      PageRequestTypes.ArchivePageSuccess,
      PageRequestTypes.ArchivePageFailure,
    ),
  archivePages: (req: ArchiveMultiRequest, force: boolean = false) =>
    createGRPCAction<typeof dxo.PagesPublic.ArchivePages>()(
      {
        type: PageRequestTypes.ArchivePagesRequest,
        grpcFunction: dxo.PagesPublic.ArchivePages,
        body: req,
        force,
        invalidateCacheKey,
        readWrite: 'write',
      },
      PageRequestTypes.ArchivePagesSuccess,
      PageRequestTypes.ArchivePagesFailure,
    ),
  computePageFlow: (
    body: PageFlowRequestBody,
    searchCacheKey: SearchCacheKey,
    uid: UID,
    batchOptions?: BatchOptions,
    force = false,
  ) => {
    const requestBody = pagesRequestBodyToComputePageFlowRequest(body);
    return createGRPCAction<typeof PageMetricsServicePublic.ComputePageFlow>({
      cacheKey: (url: string) => {
        const { cacheOptions, ...requestCacheData } = body;
        return `${url}:${hashObject(requestCacheData)}:${hashObject(searchCacheKey)}`;
      },
      uid,
    })(
      {
        type: PageRequestTypes.ComputePageFlowRequest,
        grpcFunction: PageMetricsServicePublic.ComputePageFlow,
        body: requestBody,
        readWrite: 'write',
        settings: {
          headers: { 'GRPC-Timeout': '1M' }, // allow for 1 minute before context deadline exceeded
          batchOptions,
        },
        force,
      },
      PageRequestTypes.ComputePageFlowSuccess,
      PageRequestTypes.ComputePageFlowFailure,
    );
  },
  autocomplete: (req: AutocompleteRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.Autocomplete>()(
      {
        type: PageRequestTypes.AutocompleteRequest,
        grpcFunction: dxo.PagesPublic.Autocomplete,
        body: req,
        readWrite: 'read',
      },
      PageRequestTypes.AutocompleteSuccess,
      PageRequestTypes.AutocompleteFailure,
    ),
  search: (req: SearchRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.Search>()(
      {
        type: PageRequestTypes.SearchRequest,
        grpcFunction: dxo.PagesPublic.Search,
        body: req,
        readWrite: 'read',
      },
      PageRequestTypes.SearchSuccess,
      PageRequestTypes.SearchFailure,
    ),
  match: (req: dxo.MatchRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.Match>()(
      {
        type: PageRequestTypes.MatchPageRequest,
        grpcFunction: dxo.PagesPublic.Match,
        body: req,
        readWrite: 'read',
      },
      PageRequestTypes.MatchPageSuccess,
      PageRequestTypes.MatchPageFailure,
    ),
  setMLState: (req: SetMLStateRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.SetMLState>()(
      {
        type: PageRequestTypes.SetMLStateRequest,
        grpcFunction: dxo.PagesPublic.SetMLState,
        body: req,
        readWrite: 'write',
        force: true,
      },
      PageRequestTypes.SetMLStateSuccess,
      PageRequestTypes.SetMLStateFailure,
    ),
  silenceIssue: (req: dxo.PageIssueSilenceRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.SilenceIssue>()(
      {
        type: PageRequestTypes.SilenceIssueRequest,
        grpcFunction: dxo.PagesPublic.SilenceIssue,
        body: req,
        readWrite: 'write',
        invalidateCacheKey,
      },
      PageRequestTypes.SilenceIssueSuccess,
      PageRequestTypes.SilenceIssueFailure,
    ),
  unsilenceIssue: (req: dxo.PageIssueUnSilenceRequest) =>
    createGRPCAction<typeof dxo.PagesPublic.UnSilenceIssue>()(
      {
        type: PageRequestTypes.UnsilenceIssueRequest,
        grpcFunction: dxo.PagesPublic.UnSilenceIssue,
        body: req,
        readWrite: 'write',
        invalidateCacheKey,
      },
      PageRequestTypes.UnsilenceIssueSuccess,
      PageRequestTypes.UnsilenceIssueFailure,
    ),
  getSuggestedPage: (req: dxo.GetSuggestedPageRequest, force: boolean = false) =>
    createGRPCAction<typeof dxo.PagesPublic.GetSuggestedPage>()(
      {
        type: PageRequestTypes.GetSuggestedPageRequest,
        grpcFunction: dxo.PagesPublic.GetSuggestedPage,
        body: req,
        invalidateCacheKey,
        force,
        readWrite: 'read',
      },
      PageRequestTypes.GetSuggestedPageSuccess,
      PageRequestTypes.GetSuggestedPageFailure,
    ),
  matchUrls: (req: MatchUrlsRequest, force: boolean = false) =>
    createGRPCAction<typeof PagesPublic.MatchUrls>()(
      {
        type: PageRequestTypes.MatchUrlsRequest,
        grpcFunction: PagesPublic.MatchUrls,
        body: req,
        force,
        readWrite: 'write',
      },
      PageRequestTypes.MatchUrlsSuccess,
      PageRequestTypes.MatchUrlsFailure,
    ),
};

export interface PageFlowPayload {
  body: PageFlowRequestBody;
  flags: Flags;
  searchCacheKey: SearchCacheKey;
}

const extractCacheOptions = (payload: PageFlowPayload) => getCacheOptions(payload.body);
export const PageFlowAsyncActions = {
  appCache: (uid: UID, forceSwap?: boolean) => ({
    action: createAppCacheRequestAction<ComputePageFlowResponse, PageFlowPayload>()({
      query: (
        dispatch: RequestDispatch<PageActions>,
        payload: PageFlowPayload,
        isRefetch: boolean,
      ) => {
        const cacheOptions = isRefetch ? metricsCacheOptionsRefresh : extractCacheOptions(payload);
        return dispatch(
          PagesRequestActions.computePageFlow(
            {
              ...payload.body,
              cacheOptions,
            },
            payload.searchCacheKey,
            uid,
            undefined,
            isRefetch, // if user specifically requested a refresh, force the request action
          ),
        );
      },
      loading: PageFlowActions.appCacheIsLoading,
      failed: PageFlowActions.appCacheLoadFailed,
      refetching: PageFlowActions.appCacheIsRefetching,
      hasData: PageFlowActions.appCacheHasData,
      fresh: PageFlowActions.appCacheFresh,
      isDataFresh: (payload: PageFlowPayload, data: ComputePageFlowResponse) =>
        isDataFresh(extractCacheOptions(payload), Timestamp.mustDate(data.computedAt), new Date()),
      isSimiliar: (newData: ComputePageFlowResponse, oldData: ComputePageFlowResponse) =>
        pageFlowSimilarData(newData.pageFlow, oldData.pageFlow),
      forceSwap,
    }),
    cancel: (dispatch: AppDispatch, uid: UID) => {
      dispatch(createRequestCancellationAction(uid));
    },
  }),
};
